import axios from 'axios';
import { Form } from 'formik';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledTextArea } from '../../../../components/base/forms/input';
import { DAMAGE_REPORT_VALIDATE_TN } from '../../../../constants/paths/api';
import { getCurrentLangPretty } from '../../../../helpers/url/urlHelpers';
import Button from '../../../base/button';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import { StyledErrorMessage } from '../../../base/forms/formikFieldWrapper.styles';
import FormikSelect from '../../../base/forms/formikSelect';
import { StyledFormikField } from '../../../base/forms/input';
import { Column, Columns } from '../../../base/grid';
import { H4 } from '../../../base/typography';
import UploadFiles from '../../../uploadFiles';
import { IStepInfoAboutDamage } from '../stepInfoAboutDamage.schema';
import * as StyledInfoAboutDamageForm from './stepInfoAboutDamageForm.styles';

interface Props {
	values: IStepInfoAboutDamage;
	setFieldTouched: any;
	setFieldValue: any;
	isValid: boolean;
	isSubmitting: boolean;
	errors: any;
}

const showOrHideStar = values => {
	const { damageType } = values;
	const result = damageType === 'reportDamage' ? '*' : '';
	return result;
};

const StepInfoAboutDamageForm: FC<Props> = ({ values, setFieldTouched, setFieldValue }) => {
	const { t } = useTranslation(['generalForm', 'damageReport']);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const whoFillsOutOptions = [
		{
			label: t('damageReport:who-completes-report-options.sender'),
			value: 'sender',
		},
		{
			label: t('damageReport:who-completes-report-options.receiver'),
			value: 'recipient',
		},
		{
			label: t('damageReport:who-completes-report-options.employee'),
			value: 'employee',
		},
		{
			label: t('damageReport:who-completes-report-options.other'),
			value: 'other',
		},
	];

	const howToHandle = [
		{
			label: t('damageReport:handle-damaged-items.send-item-to-claims-department'),
			value: 'sendToUs',
		},
		{
			label: t('damageReport:handle-damaged-items.receiver-keeps-item'),
			value: 'recipientIsGoingToHoldOnToTheProduct',
		},
		{
			label: t('damageReport:handle-damaged-items.sender-received-item'),
			value: 'productWithSender',
		},
		{
			label: t('damageReport:handle-damaged-items.sender-keeps-item'),
			value: 'senderIsGoingToHoldOnToTheProduct',
		},
	];

	const onWhoFillsOutChange = (key, option) => {
		if (key && option) {
			setFieldValue(key, option);
			if (option.value === 'sender' || option.value === 'recipient') {
				setFieldValue('emailForCopyOfReport', '');
			}
		}
	};

	const onTrackingNumberChange = async () => {
		if (values.trackingNumber && values.trackingNumber.length >= 8 && values.trackingNumber.length <= 35) {
			try {
				await axios
					.get(
						`${DAMAGE_REPORT_VALIDATE_TN}?shipmentId=${
							values.trackingNumber
						}&lang=${getCurrentLangPretty()}`
					)
					.then(res => {
						if (res.data?.length > 0) {
							setError(true);
							setErrorMessage(res.data);
						} else {
							setError(false);
							setErrorMessage('');
						}
					})
					.catch(errorResponse => {
						setError(errorResponse.response.data.error);
						setErrorMessage(errorResponse.response.data.errorMessage);
					});
			} catch (e) {
				setError(true);
				setErrorMessage('Óvænt villa.');
			}
		} else {
			setError(false);
			setErrorMessage('');
		}
	};

	return (
		<Form>
			<H4 marginBottom="2rem">{t('damageReport:info-about-sender')}</H4>
			<Columns>
				<Column>
					<FormikFieldWrapper
						label={t('damageReport:who-completes-report')}
						name="whoFillsOut"
						smallLabel
						languageComponent="generalForm">
						<FormikSelect
							id="whoFillsOut"
							name="whoFillsOut"
							value={values.whoFillsOut}
							options={whoFillsOutOptions}
							placeholder={t('choose')}
							onBlur={setFieldTouched}
							onChange={(e, v) => onWhoFillsOutChange(e, v)}
						/>
					</FormikFieldWrapper>
				</Column>
			</Columns>

			{values.whoFillsOut &&
				values.whoFillsOut.value &&
				(values.whoFillsOut.value === 'employee' || values.whoFillsOut.value === 'other') && (
					<Columns>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								name="reporterName"
								label={`${t('name')}*`}
								smallLabel
								languageComponent="generalForm">
								<StyledFormikField
									type="text"
									id="reporterName"
									name="reporterName"
									placeholder={t('name')}
								/>
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								name="emailForCopyOfReport"
								languageComponent="generalForm"
								label={`${t('damageReport:email-copy-of-report')}*`}
								smallLabel>
								<StyledFormikField
									type="text"
									id="emailForCopyOfReport"
									name="emailForCopyOfReport"
									placeholder={t('damageReport:email-copy-of-report')}
								/>
							</FormikFieldWrapper>
						</Column>
					</Columns>
				)}

			<H4 marginTop="4rem" marginBottom="2rem">
				{t('damageReport:info-about-damage')}
			</H4>
			<Columns>
				<Column>
					{values.damageType === 'reportDamage' && (
						<>
							<FormikFieldWrapper
								label={t('damageReport:how-to-handle')}
								name="howToHandle"
								smallLabel
								languageComponent="generalForm">
								<FormikSelect
									id="howTohandle"
									name="howToHandle"
									value={values.howToHandle}
									options={howToHandle}
									placeholder={t('choose')}
									onBlur={setFieldTouched}
									onChange={setFieldValue}
								/>
							</FormikFieldWrapper>
							<StyledInfoAboutDamageForm.Details>
								{values.howToHandle &&
									values.howToHandle.value === 'sendToUs' &&
									t('damageReport:handle-damaged-items.send-item-to-claims-department-info')}
								{values.howToHandle &&
									values.howToHandle.value === 'recipientIsGoingToHoldOnToTheProduct' &&
									t('damageReport:handle-damaged-items.receiver-keeps-item-info')}
								{values.howToHandle &&
									values.howToHandle.value === 'senderIsGoingToHoldOnToTheProduct' &&
									t('damageReport:handle-damaged-items.sender-keeps-item-info')}
							</StyledInfoAboutDamageForm.Details>
						</>
					)}
				</Column>
			</Columns>

			<Columns>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						label={`${t('trackingnumber')} *`}
						name="trackingNumber"
						smallLabel
						languageComponent="generalForm">
						<StyledFormikField
							type="text"
							id="trackingNumber"
							name="trackingNumber"
							onKeyUp={() => onTrackingNumberChange()}
							placeholder={t('trackingnumber')}
						/>
					</FormikFieldWrapper>
					{error && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
				</Column>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						label={`${t('damageReport:content')}*`}
						name="content"
						smallLabel
						languageComponent="generalForm">
						<StyledFormikField
							type="text"
							id="content"
							name="content"
							placeholder={t('damageReport:content')}
						/>
					</FormikFieldWrapper>
				</Column>
				<Column tablet={1 / 1}>
					<FormikFieldWrapper
						label={`${t('damageReport:description-of-damage')} *`}
						name="description"
						smallLabel
						languageComponent="generalForm">
						<StyledTextArea
							type="text"
							component="textarea"
							id="description"
							name="description"
							padding="1rem"
							placeholder={t('damageReport:description-of-damage-placeholder')}
						/>
					</FormikFieldWrapper>
				</Column>
			</Columns>
			<StyledInfoAboutDamageForm.UploadFilesWrapper>
				<H4 marginTop="4rem" marginBottom="2rem">{`${t('damageReport:add-receipt')}*`}</H4>
				<UploadFiles label={t('damageReport:add-receipt-info-text')} smallLabel name="addReceipts" />
				<H4 marginTop="4rem" marginBottom="2rem">
					{`${t('damageReport:add-picture')}${showOrHideStar(values)}`}
				</H4>
				<UploadFiles label={t('damageReport:add-picture-info-text')} smallLabel name="addPictures" />
			</StyledInfoAboutDamageForm.UploadFilesWrapper>
			<StyledInfoAboutDamageForm.ButtonWrapper>
				<Button wide primary right type="submit" disabled={error}>
					{t('next')}
				</Button>
			</StyledInfoAboutDamageForm.ButtonWrapper>
		</Form>
	);
};

export default StepInfoAboutDamageForm;
